import { useRef } from 'react';
import { Combobox, Field, Input, Select } from '@/components/ui';
import type { VariantDraft } from './SequenceStepsEditor';

export interface VariantTemplateOption {
  id: number;
  name: string;
  subject: string | null;
}

export interface VariantEditorProps {
  variant: VariantDraft;
  /** Visible heading, e.g. "Variant A". Omit when the step shows one variant. */
  heading?: string;
  templates: readonly VariantTemplateOption[];
  onChange(patch: Partial<Omit<VariantDraft, 'key' | 'id' | 'label'>>): void;
  disabled?: boolean;
  errors?: { subject?: string; preheader?: string; templateId?: string };
}

/**
 * The variables offered by the subject's insert menu. Common keys only — the
 * full catalogue lives with the template builder; a subject can still name any
 * other `{{key}}` by typing it.
 */
const SUBJECT_VARIABLES: readonly { token: string; label: string }[] = [
  { token: '{{contact.first_name}}', label: 'Contact first name' },
  { token: '{{contact.last_name}}', label: 'Contact last name' },
  { token: '{{contact.full_name}}', label: 'Contact full name' },
  { token: '{{contact.email}}', label: 'Contact email' },
  { token: '{{company.name}}', label: 'Company name' },
  { token: '{{company.short_name}}', label: 'Company short name' },
];

/**
 * One email's subject, preheader and template. Used once per step, and twice
 * side by side on an A/B broadcast.
 */
export function VariantEditor({
  variant,
  heading,
  templates,
  onChange,
  disabled,
  errors = {},
}: VariantEditorProps) {
  const subjectRef = useRef<HTMLInputElement>(null);

  const templateOptions = templates.map((t) => ({ value: String(t.id), label: t.name }));
  // A stored template the list no longer carries keeps its row, so the trigger
  // never reads as empty over a real value.
  if (variant.templateId !== null && !templates.some((t) => t.id === variant.templateId)) {
    templateOptions.unshift({
      value: String(variant.templateId),
      label: `Template #${variant.templateId} (not in the list)`,
    });
  }

  function chooseTemplate(raw: string) {
    const id = raw === '' ? null : Number(raw);
    const patch: Partial<Omit<VariantDraft, 'key' | 'id' | 'label'>> = { templateId: id };
    if (id !== null && variant.subject.trim() === '') {
      const subject = templates.find((t) => t.id === id)?.subject;
      if (subject) patch.subject = subject;
    }
    onChange(patch);
  }

  function insertVariable(token: string) {
    if (!token) return;
    const input = subjectRef.current;
    const current = variant.subject;
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    onChange({ subject: next });
    requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      const caret = start + token.length;
      input.setSelectionRange(caret, caret);
    });
  }

  return (
    <div className="grid min-w-0 content-start gap-3">
      {heading && <h4 className="m-0 text-xs font-semibold text-fg-secondary">{heading}</h4>}

      <Field label="Subject" error={errors.subject}>
        {({ id, 'aria-describedby': describedBy }) => (
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1">
              <Input
                ref={subjectRef}
                id={id}
                aria-describedby={describedBy}
                value={variant.subject}
                invalid={Boolean(errors.subject)}
                disabled={disabled}
                onChange={(e) => onChange({ subject: e.target.value })}
              />
            </div>
            {!disabled && (
              <div className="sm:w-44">
                <Select
                  aria-label={`Insert a variable into the ${heading ? `${heading} ` : ''}subject`}
                  value=""
                  onChange={(e) => insertVariable(e.target.value)}
                >
                  <option value="">Insert variable…</option>
                  {SUBJECT_VARIABLES.map((v) => (
                    <option key={v.token} value={v.token}>
                      {v.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        )}
      </Field>

      <Field label="Preheader" error={errors.preheader}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={variant.preheader}
            invalid={Boolean(errors.preheader)}
            disabled={disabled}
            onChange={(e) => onChange({ preheader: e.target.value })}
          />
        )}
      </Field>

      <Field label="Template" error={errors.templateId}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Combobox
            id={id}
            aria-describedby={describedBy}
            value={variant.templateId === null ? '' : String(variant.templateId)}
            onChange={chooseTemplate}
            options={templateOptions}
            placeholder="Choose a template"
            emptyOptionLabel="No template"
            searchPlaceholder="Search templates"
            invalid={Boolean(errors.templateId)}
            disabled={disabled}
          />
        )}
      </Field>
    </div>
  );
}
